import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { colors, authColors } from '../config/branding';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

function getScrollbarStyles(mode: ThemeMode) {
  const track = mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const thumb = mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(74,144,226,0.35)';
  const thumbHover = mode === 'dark' ? 'rgba(255,255,255,0.32)' : colors.primary;

  return {
    scrollbarWidth: 'thin' as const,
    scrollbarColor: `${thumb} ${track}`,
    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: track,
      borderRadius: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: thumb,
      borderRadius: 8,
      border: `2px solid ${track}`,
      backgroundClip: 'padding-box',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: thumbHover,
    },
    '&::-webkit-scrollbar-corner': {
      backgroundColor: track,
    },
  };
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme-mode');
    return (saved as ThemeMode) || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary,
        light: mode === 'dark' ? '#64b5f6' : '#42a5f5',
        dark: '#1565c0',
        contrastText: '#ffffff',
      },
      secondary: {
        main: colors.secondary,
        light: mode === 'dark' ? '#90caf9' : '#64b5f6',
        dark: '#1976d2',
        contrastText: '#ffffff',
      },
      background: {
        default: mode === 'dark' ? authColors.background : '#f0f2f5',
        paper: mode === 'dark' ? authColors.card : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? authColors.text : '#2c3e50',
        secondary: mode === 'dark' ? authColors.muted : '#5a6c7d',
      },
      divider: mode === 'dark' ? authColors.border : 'rgba(0,0,0,0.15)',
      action: {
        hover: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(74,144,226,0.08)',
        selected: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(74,144,226,0.12)',
        disabled: mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.38)',
        disabledBackground: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)',
      },
      success: {
        main: '#4caf50',
        light: '#81c784',
        dark: '#388e3c',
      },
      warning: {
        main: '#ff9800',
        light: '#ffb74d',
        dark: '#f57c00',
      },
      error: {
        main: '#f44336',
        light: '#ef5350',
        dark: '#c62828',
      },
      info: {
        main: '#2196f3',
        light: '#64b5f6',
        dark: '#1976d2',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: 14,
      h1: {
        fontWeight: 700,
        fontSize: '3rem',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 1.2,
        letterSpacing: '-0.015em',
      },
      h3: {
        fontWeight: 600,
        fontSize: '2rem',
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.3,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.4,
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.4,
      },
      button: {
        fontWeight: 500,
        fontSize: '0.875rem',
        textTransform: 'none',
      },
    },
    spacing: 8,
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: mode === 'dark' ? authColors.background : '#f0f2f5',
            backgroundImage:
              mode === 'dark'
                ? 'repeating-linear-gradient(90deg, rgba(217,217,217,0.02) 0px, rgba(217,217,217,0.02) 1px, transparent 1px, transparent 20px), repeating-linear-gradient(0deg, rgba(217,217,217,0.02) 0px, rgba(217,217,217,0.02) 1px, transparent 1px, transparent 20px)'
                : 'none',
          },
          '*': getScrollbarStyles(mode),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 12,
            fontWeight: 600,
            padding: '10px 20px',
          },
          contained: {
            boxShadow:
              mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.25)' : '0 2px 10px rgba(74,144,226,0.25)',
            '&:hover': {
              boxShadow:
                mode === 'dark'
                  ? '0 6px 16px rgba(0,0,0,0.35)'
                  : '0 4px 14px rgba(74,144,226,0.35)',
            },
          },
          outlined: {
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border:
              mode === 'dark' ? `1px solid ${authColors.border}` : '1px solid rgba(0,0,0,0.12)',
            boxShadow:
              mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.06)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              backgroundColor: mode === 'dark' ? authColors.inputBg : '#ffffff',
              '& fieldset': {
                borderWidth: '2px',
                borderColor: mode === 'dark' ? authColors.border : 'rgba(0,0,0,0.15)',
              },
              '&:hover fieldset': {
                borderColor: mode === 'dark' ? 'rgba(217,217,217,0.5)' : 'rgba(74,144,226,0.5)',
              },
              '&.Mui-focused fieldset': {
                borderColor: mode === 'dark' ? authColors.text : colors.primary,
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          input: {
            '&[type=number]': {
              MozAppearance: 'textfield',
            },
            '&[type=number]::-webkit-outer-spin-button, &[type=number]::-webkit-inner-spin-button':
              {
                WebkitAppearance: 'none',
                margin: 0,
              },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(74,144,226,0.08)',
              fontWeight: 700,
              color: mode === 'dark' ? authColors.text : '#2c3e50',
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(74,144,226,0.04)',
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            boxShadow:
              mode === 'dark' ? '8px 0 32px rgba(0,0,0,0.4)' : '4px 0 20px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? 'rgba(10, 14, 24, 0.72)' : 'rgba(26, 31, 43, 0.45)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: mode === 'dark' ? authColors.card : '#ffffff',
            borderRadius: 16,
            border:
              mode === 'dark'
                ? `1px solid ${authColors.border}`
                : '1px solid rgba(74,144,226,0.14)',
            boxShadow:
              mode === 'dark'
                ? '0 24px 48px rgba(0,0,0,0.42), 0 0 0 1px rgba(255,255,255,0.04)'
                : '0 20px 40px rgba(74,144,226,0.14), 0 4px 16px rgba(0,0,0,0.06)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.125rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: mode === 'dark' ? authColors.text : '#2c3e50',
            padding: '20px 24px',
            borderBottom: `1px solid ${mode === 'dark' ? authColors.border : 'rgba(0,0,0,0.08)'}`,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '24px',
            backgroundColor: mode === 'dark' ? authColors.card : '#ffffff',
            color: mode === 'dark' ? authColors.text : '#2c3e50',
            '.MuiDialogTitle-root + &': {
              paddingTop: '24px',
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px',
            gap: 12,
            borderTop: `1px solid ${mode === 'dark' ? authColors.border : 'rgba(0,0,0,0.08)'}`,
            backgroundColor: mode === 'dark' ? authColors.card : '#ffffff',
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
